// Chat theme customizer
// Controls the appearance of all messaging interfaces (DMs and group chats)

import React, { useState, useEffect } from 'react';
import ThemeCustomizerModal, { ThemeCustomization } from './ThemeCustomizerModal';
import { getThemeAnimations } from '@theme/animation-config';
import type { ThemeAnimations } from '@theme/animation-config';
import { PRESET_THEMES } from '@theme/tokens';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  chatId?: string; // Optional: if you want per-chat themes in the future
};

// Use ocean-turtle preset as the default
const DEFAULT_CUSTOMIZATION: ThemeCustomization = {
  ...PRESET_THEMES['ocean-turtle'],
  navbarOpacity: 92,
  tabBarOpacity: 88,
  cardGradientFavorability: 50,
  cardOpacity: 100,
  cardGradientStrength: 100,
  gradientEnabled: true,
  gradientAngle: 135,
  gradientFavorability: 50,
  gradientStrength: 100,
  bubbleColor: PRESET_THEMES['ocean-turtle'].accentColor,
  turtleColor: PRESET_THEMES['ocean-turtle'].cardGradientColor2 || '#00897b',
  coralColors: [
    PRESET_THEMES['ocean-turtle'].accentColor,
    PRESET_THEMES['ocean-turtle'].secondaryColor || '#00acc1',
    PRESET_THEMES['ocean-turtle'].cardGradientColor2 || '#00897b',
    PRESET_THEMES['ocean-turtle'].cardGradientColor1 || '#00695c',
    PRESET_THEMES['ocean-turtle'].primaryColor || '#006064'
  ],
  profileFrameColor: PRESET_THEMES['ocean-turtle'].accentColor,
  profileFrameShape: 'square',
  profileNameAlign: 'left',
  profileFrameDesign: 'coral',
  animationsEnabled: true,
  selectedCharacter: 'turtle',
  selectedDecoration: 'coral',
  focusAnimationEnabled: false,
};

export default function ChatThemeCustomizer({ isOpen, onClose, userId, chatId }: Props) {
  const [customization, setCustomization] = useState<ThemeCustomization>(DEFAULT_CUSTOMIZATION);
  const [animations, setAnimations] = useState<ThemeAnimations>(getThemeAnimations('ocean-turtle'));
  const [animationsEnabled, setAnimationsEnabled] = useState(true);

  // Load saved chat theme on mount
  useEffect(() => {
    const loadChatTheme = async () => {
      try {
        const response = await fetch(`/users/${userId}/chat-theme`);
        if (response.ok) {
          const data = await response.json();
          if (data.customization) setCustomization(data.customization);
          if (data.animations) setAnimations(data.animations);
          if (typeof data.animationsEnabled === 'boolean') setAnimationsEnabled(data.animationsEnabled);
        }
      } catch (error) {
      }
    };

    if (isOpen) {
      loadChatTheme();
    }
  }, [isOpen, userId, chatId]);

  const handleSave = async () => {
    try {
      const response = await fetch(`/users/${userId}/chat-theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customization,
          animations,
          animationsEnabled,
        }),
      });

      if (response.ok) {
        alert('Chat theme saved successfully!');
        // Trigger a global chat theme update event
        window.dispatchEvent(new CustomEvent('chatThemeUpdated', {
          detail: { customization, animations, animationsEnabled }
        }));
        onClose();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save chat theme');
      }
    } catch (error) {
      alert('Error saving chat theme');
    }
  };

  const handleReset = () => {
    setCustomization(DEFAULT_CUSTOMIZATION);
    setAnimations(getThemeAnimations('ocean-turtle'));
    setAnimationsEnabled(true);
  };

  return (
    <ThemeCustomizerModal
      isOpen={isOpen}
      onClose={onClose}
      customization={customization}
      setCustomization={setCustomization}
      animations={animations}
      setAnimations={setAnimations}
      animationsEnabled={animationsEnabled}
      setAnimationsEnabled={setAnimationsEnabled}
      onSave={handleSave}
      onReset={handleReset}
      title="Chat Theme Customizer"
      scopeName="messages"
      showProfileOptions={false}
      showUploadOptions={false}
    />
  );
}
